import { useState } from 'react';
import type { Schema } from '../api/client';
import { ApiError } from '../api/session';
import { Button, Empty, Field, Icon, Search, Table } from '../ui';
import {
  AdminConfirm,
  AdminForm,
  AdminLink,
  AdminSelect,
  InfoRows,
  Missing,
  QueryState,
  dateTimeLabel,
  text,
  useAdminAction,
  useAdminList,
  useAdminNavigate,
  useAdministrative,
  useAdministrativeMe,
} from './common';
import { AdministrativeList, CompactFilter } from './list-layout';

type Project = Schema<'AdminProject'>;
type Dictionary = Schema<'AdminDictionary'>;
type DictionaryKind = NonNullable<Schema<'AdminReferenceCreate'>['kind']>;
const dictionaryLabels: Record<DictionaryKind, string> = {
  PERIOD: 'Периоды оплаты',
  METHOD: 'Способы оплаты',
  STATUS: 'Рабочие статусы',
  RELEVANCE: 'Актуальность',
  SERVICE_TYPE: 'Типы услуг',
};
const unitLabels = { DAY: 'дней', MONTH: 'месяцев', YEAR: 'лет' };

export function AdministrativeRegistry({
  kind,
  id,
  operation,
}: {
  kind: 'projects' | 'dictionaries';
  id?: string;
  operation?: string;
}) {
  const me = useAdministrative();
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const rows = kind === 'projects' ? me.projects : me.dictionaries;
  if (!me.allowedActions.includes('settings.read'))
    return (
      <AdministrativeList>
        <Empty title="Справочники недоступны" />
      </AdministrativeList>
    );
  if (id === 'new' || operation === 'edit') {
    const record = rows.find((row) => row.id === id);
    if (id !== 'new' && !record) return <Missing />;
    return <ReferenceForm key={`${kind}:${id}`} kind={kind} record={record} />;
  }
  const selected =
    kind === 'projects' ? rows : me.dictionaries.filter((row) => !filter || row.kind === filter);
  const visible = selected.filter(
    (row) => !search || row.name.toLocaleLowerCase('ru').includes(search.toLocaleLowerCase('ru')),
  );
  return (
    <AdministrativeList
      count={rows.length}
      toolbar={
        <>
          <Search
            value={search}
            onChange={setSearch}
            placeholder={kind === 'projects' ? 'Поиск проектов' : 'Поиск по названию значения'}
          />
          {kind === 'dictionaries' && (
            <CompactFilter
              label="Тип"
              allLabel="Все справочники"
              value={filter}
              options={Object.entries(dictionaryLabels).map(([id, name]) => ({ id, name }))}
              onChange={setFilter}
            />
          )}
          <span className="toolbar-spacer" />
          <AdminLink path="settings" className="pill">
            Настройки базы
          </AdminLink>
          <AdminLink path={kind === 'projects' ? 'dictionaries' : 'projects'} className="pill">
            {kind === 'projects' ? 'Справочники' : 'Проекты'}
          </AdminLink>
          {me.allowedActions.includes('settings.edit') && (
            <AdminLink path={`${kind}/new`} className="pill">
              <Icon name="Plus" size={16} />
              {kind === 'projects' ? 'Добавить проект' : 'Добавить значение'}
            </AdminLink>
          )}
        </>
      }
    >
      <Table
        rows={visible}
        columns={[
          { key: 'name', label: 'Название' },
          ...(kind === 'dictionaries'
            ? [
                {
                  key: 'kind',
                  label: 'Тип',
                  sortable: false,
                  render: (row: Project | Dictionary) =>
                    'kind' in row ? dictionaryLabels[(row as Dictionary).kind] : null,
                },
                {
                  key: 'duration',
                  label: 'Правило',
                  sortable: false,
                  render: (row: Project | Dictionary) =>
                    'kind' in row ? <DictionaryMeaning value={row as Dictionary} /> : null,
                },
              ]
            : []),
          {
            key: 'actions',
            label: '',
            sortable: false,
            render: (row) =>
              row.allowedActions.includes('settings.edit') ? (
                <AdminLink path={`${kind}/${row.id}/edit`}>Изменить</AdminLink>
              ) : null,
          },
        ]}
      />
    </AdministrativeList>
  );
}
function DictionaryMeaning({ value }: { value: Dictionary }) {
  return value.kind === 'PERIOD' ? (
    <>
      {value.durationCount} {value.durationUnit ? unitLabels[value.durationUnit] : ''}
    </>
  ) : value.kind === 'RELEVANCE' ? (
    <>{value.isRelevant ? 'Актуальные ресурсы' : 'Неактуальные ресурсы'}</>
  ) : null;
}
function ReferenceForm({
  kind,
  record,
}: {
  kind: 'projects' | 'dictionaries';
  record?: Project | Dictionary;
}) {
  const me = useAdministrative();
  const action = useAdminAction();
  const navigate = useAdminNavigate();
  const current = useAdministrativeMe();
  const [snapshot, setSnapshot] = useState(record);
  const dictionary = snapshot && 'kind' in snapshot ? (snapshot as Dictionary) : undefined;
  const [name, setName] = useState(record?.name || '');
  const [durationCount, setDurationCount] = useState(String(dictionary?.durationCount || 1));
  const [type, setType] = useState<DictionaryKind>(dictionary?.kind || 'PERIOD');
  const [unit, setUnit] = useState(dictionary?.durationUnit || 'MONTH');
  const [relevant, setRelevant] = useState(dictionary?.isRelevant === false ? 'false' : 'true');
  const [remove, setRemove] = useState(false);
  const [reloading, setReloading] = useState(false);
  if (
    !me.allowedActions.includes('settings.edit') ||
    (record && !record.allowedActions.includes('settings.edit'))
  )
    return <Empty title="Изменение справочников недоступно" />;
  return (
    <>
      <AdminForm
        title={`${snapshot ? 'Изменить' : 'Добавить'} ${kind === 'projects' ? 'проект' : 'значение справочника'}`}
        action={{ ...action, pending: action.pending || reloading }}
        cancel={kind}
        submit={async (data) => {
          const body: Schema<'AdminReferenceCreate'> = {
            name: text(data, 'name'),
            ...(kind === 'dictionaries'
              ? {
                  kind: type,
                  ...(type === 'PERIOD'
                    ? {
                        durationUnit: unit as 'DAY' | 'MONTH' | 'YEAR',
                        durationCount: Number(text(data, 'durationCount')),
                      }
                    : {}),
                  ...(type === 'RELEVANCE' ? { isRelevant: relevant === 'true' } : {}),
                }
              : {}),
          };
          const result = await action.run<Project | Dictionary>(
            snapshot ? 'PATCH' : 'POST',
            kind === 'projects'
              ? snapshot
                ? '/api/v1/administrative/projects/{id}'
                : '/api/v1/administrative/projects'
              : snapshot
                ? '/api/v1/administrative/dictionaries/{id}'
                : '/api/v1/administrative/dictionaries',
            { ...body, ...(snapshot ? { version: snapshot.version } : {}) },
            snapshot ? { id: snapshot.id } : {},
          );
          if (result) await navigate(kind);
        }}
      >
        {record &&
          (record.version !== snapshot?.version ||
            (action.error instanceof ApiError && action.error.status === 409)) && (
            <div role="status">
              <p>Другой сотрудник изменил запись. Ваши введённые значения сохранены.</p>
              <Button
                disabled={action.pending || reloading}
                onClick={async () => {
                  setReloading(true);
                  try {
                    const result = await current.refetch();
                    if (result.error || !result.data) return;
                    const latest = (
                      kind === 'projects' ? result.data.projects : result.data.dictionaries
                    ).find((item) => item.id === record.id);
                    if (!latest) return;
                    const latestDictionary = 'kind' in latest ? (latest as Dictionary) : undefined;
                    setSnapshot(latest);
                    setName(latest.name);
                    setType(latestDictionary?.kind || 'PERIOD');
                    setUnit(latestDictionary?.durationUnit || 'MONTH');
                    setDurationCount(String(latestDictionary?.durationCount || 1));
                    setRelevant(latestDictionary?.isRelevant === false ? 'false' : 'true');
                    setRemove(false);
                    action.setError(null);
                  } finally {
                    setReloading(false);
                  }
                }}
              >
                Загрузить новую версию
              </Button>
            </div>
          )}
        <Field
          label="Название"
          name="name"
          required
          maxLength={200}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        {kind === 'dictionaries' && (
          <>
            {dictionary ? (
              <InfoRows rows={[['Тип справочника', dictionaryLabels[dictionary.kind]]]} />
            ) : (
              <AdminSelect
                label="Тип справочника"
                value={type}
                onChange={(value) => setType(value as DictionaryKind)}
                options={Object.entries(dictionaryLabels).map(([id, name]) => ({ id, name }))}
                required
              />
            )}
            {type === 'PERIOD' && (
              <>
                <Field
                  label="Длительность"
                  name="durationCount"
                  type="number"
                  min={1}
                  max={366}
                  step={1}
                  required
                  value={durationCount}
                  onChange={(event) => setDurationCount(event.target.value)}
                />
                <AdminSelect
                  label="Единица периода"
                  value={unit}
                  onChange={(value) => setUnit(value as typeof unit)}
                  options={[
                    { id: 'DAY', name: 'Дни' },
                    { id: 'MONTH', name: 'Месяцы' },
                    { id: 'YEAR', name: 'Годы' },
                  ]}
                  required
                />
                <p className="muted">
                  Период используется для расчёта продлений и календаря оплат. Даты вычисляются по
                  календарю платформы.
                </p>
              </>
            )}
            {type === 'RELEVANCE' && (
              <>
                <AdminSelect
                  label="К каким ресурсам относится значение"
                  value={relevant}
                  onChange={setRelevant}
                  options={[
                    { id: 'true', name: 'К актуальным ресурсам' },
                    { id: 'false', name: 'К неактуальным ресурсам' },
                  ]}
                  required
                />
                <p className="muted">
                  Актуальность показывает, нужен ли ресурс компании. Рабочий статус, оплата, архив и
                  корзина сохраняются отдельно.
                </p>
              </>
            )}
          </>
        )}
        {kind === 'projects' && (
          <p className="muted">
            Переименование обновит название проекта во всех связанных карточках.
          </p>
        )}
      </AdminForm>
      {snapshot && (
        <div className="admin-separated">
          <Button onClick={() => setRemove(true)}>
            Удалить {kind === 'projects' ? 'проект' : 'значение'}
          </Button>
        </div>
      )}
      {remove && snapshot && (
        <AdminConfirm
          title="Удалить значение?"
          action={action}
          onClose={() => setRemove(false)}
          onConfirm={async () => {
            const result = await action.run(
              'DELETE',
              kind === 'projects'
                ? '/api/v1/administrative/projects/{id}'
                : '/api/v1/administrative/dictionaries/{id}',
              { version: snapshot.version },
              { id: snapshot.id },
              'Значение удалено',
            );
            if (result) await navigate(kind);
          }}
          label="Удалить"
        >
          <p>
            «{snapshot.name}» можно удалить после изменения связанных ресурсов. История оплат
            сохраняется.
          </p>
        </AdminConfirm>
      )}
    </>
  );
}

type Audit = Schema<'AdminAuditPage'>['items'][number];
const auditActions: Record<string, string> = {
  'resource.created': 'Создан ресурс',
  'resource.updated': 'Изменён ресурс',
  'resource.archive': 'Ресурс архивирован',
  'resource.unarchive': 'Ресурс возвращён в работу',
  'resource.trash': 'Ресурс перемещён в корзину',
  'resource.restore': 'Ресурс восстановлен',
  'resource.purged': 'Ресурс удалён окончательно',
  'resource.renewed': 'Зарегистрировано продление',
  'credentials.revealed': 'Просмотрены данные подключения',
  'resource.linked': 'Добавлена связь ресурсов',
  'resource.unlinked': 'Удалена связь ресурсов',
  'member.updated': 'Изменён доступ сотрудника',
  'invite.created': 'Создано приглашение',
  'invite.revoked': 'Приглашение отозвано',
  'role.created': 'Создана роль',
  'role.updated': 'Изменена роль',
  'role.deleted': 'Удалена роль',
  'provider.created': 'Создан аккаунт поставщика',
  'provider.updated': 'Изменён аккаунт поставщика',
  'provider.deleted': 'Удалён аккаунт поставщика',
  'card.created': 'Добавлена карта',
  'card.updated': 'Изменена карта',
  'card.deleted': 'Удалена карта',
  'balance.updated': 'Изменён баланс',
  'section.created': 'Создан раздел',
  'section.updated': 'Изменён раздел',
  'section.deleted': 'Удалён раздел',
  'sheet.created': 'Создан лист',
  'sheet.updated': 'Изменён лист',
  'sheet.deleted': 'Удалён лист',
  'project.created': 'Добавлен проект',
  'project.updated': 'Изменён проект',
  'project.deleted': 'Удалён проект',
  'dictionary.created': 'Добавлено значение справочника',
  'dictionary.updated': 'Изменено значение справочника',
  'dictionary.deleted': 'Удалено значение справочника',
  'resource.bulk_update': 'Массовое изменение',
  'resource.bulk_archive': 'Массовое архивирование',
  'resource.bulk_trash': 'Массовое удаление',
  'resource.bulk_renew': 'Массовое продление',
  'resource.sheet_resolution': 'Изменена связь с листом',
  'resource.provider_resolution': 'Изменена связь с поставщиком',
};
const fieldLabels: Record<string, string> = {
  name: 'Название',
  metadata: 'Поля ресурса',
  customValues: 'Дополнительные колонки',
  cost: 'Стоимость',
  currency: 'Валюта',
  paidUntil: 'Срок оплаты',
  paymentPeriodId: 'Период оплаты',
  paymentMethodId: 'Способ оплаты',
  projectId: 'Проект',
  providerAccountId: 'Аккаунт поставщика',
  roleId: 'Роль',
  accountId: 'Аккаунт',
  rules: 'Разрешения',
  blockedAt: 'Блокировка',
  credentialsChanged: 'Данные подключения',
  businessPayload: 'Поля карточки',
  links: 'Связи',
  comment: 'Комментарий',
  relevanceId: 'Актуальность',
  statusId: 'Статус',
  responsibleAccountId: 'Ответственный',
  autoRenew: 'Автопродление',
  payment: 'Оплата',
  version: 'Версия',
  deletedAt: 'Удаление',
  template: 'Шаблон',
  kind: 'Тип',
  customColumns: 'Колонки',
  last4: 'Последние 4 цифры карты',
  balance: 'Баланс',
  login: 'Логин',
  serviceUrl: 'Ссылка поставщика',
  durationUnit: 'Единица периода',
  durationCount: 'Длительность',
  isRelevant: 'Правило актуальности',
  revokedAt: 'Отзыв',
  archive: 'Архивирование',
  unarchive: 'Возврат из архива',
  trash: 'Перемещение в корзину',
  restore: 'Восстановление',
  renew: 'Продление',
  update: 'Изменение',
  sheetId: 'Лист',
};
export function AdministrativeAudit() {
  const me = useAdministrative();
  const query = useAdminList<Audit>('/api/v1/administrative/audit');
  const [search, setSearch] = useState('');
  const visible = query.items.filter(
    (row) =>
      !search ||
      `${row.actorLogin || ''} ${auditActions[row.action] || ''} ${row.changedFields.map((field) => fieldLabels[field] || '').join(' ')}`
        .toLocaleLowerCase('ru')
        .includes(search.toLocaleLowerCase('ru')),
  );
  return (
    <AdministrativeList
      count={`${query.items.length}${query.hasNextPage ? '+' : ''}`}
      toolbar={
        <>
          <Search
            value={search}
            onChange={setSearch}
            placeholder="Поиск по сотруднику или действию"
          />
          <span className="toolbar-spacer" />
          <Button onClick={() => void query.refetch()}>Обновить</Button>
        </>
      }
    >
      <QueryState query={query} />
      {!query.error && (
        <Table
          rows={visible}
          loading={query.isLoading}
          hasMore={query.hasNextPage}
          loadMore={() => void query.fetchNextPage()}
          columns={[
            {
              key: 'createdAt',
              label: 'Дата и время',
              render: (row) => dateTimeLabel(row.createdAt, me.timezone),
            },
            {
              key: 'actorAccountId',
              label: 'Сотрудник',
              render: (row) => row.actorLogin || 'Сотрудник недоступен',
            },
            {
              key: 'action',
              label: 'Действие',
              render: (row) => auditActions[row.action] || 'Изменение настроек базы',
            },
            {
              key: 'objectId',
              label: 'Объект',
              render: (row) =>
                row.objectType === 'Resource' && row.objectId ? (
                  <AdminLink path={`resources/${row.objectId}`}>Карточка ресурса</AdminLink>
                ) : row.objectType === 'Role' ? (
                  'Роль'
                ) : row.objectType === 'Member' ? (
                  'Участник'
                ) : row.objectType === 'Invite' ? (
                  'Приглашение'
                ) : row.objectType === 'Provider' ? (
                  'Аккаунт поставщика'
                ) : row.objectType === 'Sheet' ? (
                  'Лист'
                ) : row.objectType === 'Section' ? (
                  'Раздел'
                ) : row.objectType === 'project' ? (
                  'Проект'
                ) : row.objectType === 'dictionary' ? (
                  'Справочник'
                ) : row.objectType === 'Card' ? (
                  'Карта'
                ) : (
                  'Настройки'
                ),
            },
            {
              key: 'changedFields',
              label: 'Изменённые поля',
              render: (row) =>
                row.changedFields
                  .map(
                    (field) =>
                      fieldLabels[field] ||
                      (['USD', 'EUR', 'RUB'].includes(field) ? field : 'Поле'),
                  )
                  .join(', ') || 'Без изменения данных',
            },
          ]}
        />
      )}
    </AdministrativeList>
  );
}
